import React, { useEffect, useRef, useState } from 'react'
import Modal from 'react-modal'
import styles from './selectAvatarModal.module.scss'
import UploadImage from './UploadImage'
import BtnSecondary from 'components/generic/BtnSecondary'
import BtnPrimary from 'components/generic/BtnPrimary'
import { Tab, Tabs } from 'react-bootstrap'
import IcnStyleSwitcher from './IcnStyleSwitcher'
import { People, Rackets } from 'components/Icons'
import ITFAvatarSelector from './ITFAvatarSelector'
import i18n from 'constants/strings'
import { ImageType } from 'react-images-uploading'
import AvatarEditor from 'react-avatar-editor'
import imageCompression from 'browser-image-compression'
import { toast } from 'react-toastify'
import { AccountDetails } from 'types/global'

async function createImageFile(dataURL: string) {
  const blob = await (await fetch(dataURL)).blob()
  const file = new File([blob], 'avatar.png', { type: blob.type })
  return file
}

enum SelectAvatarTabs {
  UPLOAD = 'UPLOAD',
  PICK_ITF_AVATAR = 'PICK_ITF_AVATAR',
}

export const action = { key: 'Action', icon: <Rackets /> }
export const people = { key: 'People', icon: <People /> }

interface Props {
  show: boolean
  onClose: () => void
  uploadDisabled?: boolean
  appElement: string
  onUploadFile: (file: File) => void
  onUpdateAvatar: (avatarUrl: string) => void
  currentAvatarUrl: string | null
}

const SelectItfAvatarModal = ({ show, onClose, uploadDisabled = false, appElement, onUploadFile, onUpdateAvatar, currentAvatarUrl }: Props) => {
  const [modalOpen, setModalOpen] = useState(false)
  const handleCloseModal = () => {
    setModalOpen(false)
    onClose()
  }
  const [selectedTab, setSelectedTab] = useState<SelectAvatarTabs>(
    uploadDisabled ? SelectAvatarTabs.PICK_ITF_AVATAR : SelectAvatarTabs.UPLOAD
  )
  const [ITFAvatarView, setITFAvatarView] = useState<string>(action.key)
  const toggleITFAvatarView = key => {
    key === action.key ? setITFAvatarView(action.key) : setITFAvatarView(people.key)
  }
  const editorRef = useRef<AvatarEditor | null>(null)
  const [loadedImages, setLoadedImages] = useState<ImageType[]>([])
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null)

  useEffect(() => {
    setModalOpen(show)
  }, [setModalOpen, show])

  useEffect(() => {
    if (selectedTab !== SelectAvatarTabs.PICK_ITF_AVATAR) setAvatarUrl(null)
  }, [selectedTab])

  const SetUploadedAvatarButton = () => (
    <BtnPrimary
      size='small'
      onClick={async () => {
        if (editorRef.current) {
          try {
            const cropCanvas = editorRef.current.getImageScaledToCanvas()
            const imageFile = await createImageFile(cropCanvas.toDataURL())
            const blob = await imageCompression(imageFile, {
              maxSizeMB: 0.1,
              useWebWorker: true,
            })
            const file = new File([blob], blob.name, { type: blob.type })
            onUploadFile(file)
          } catch (e) {
            console.warn('Failed to create or compress image: ', e && (e as Error)?.message)
            toast.error(i18n.t('strAlerts.avatarNotUploaded'))
          }
        }
        handleCloseModal()
      }}
      isDisabled={!loadedImages.length}
    >
      {i18n.t('strCommonUI.button.update')}
    </BtnPrimary>
  )

  const SetSelectedAvatarButton = () => (
    <BtnPrimary
      size='small'
      onClick={() => {
        if (avatarUrl) {
          onUpdateAvatar(avatarUrl)
          setAvatarUrl(null)
        }
        handleCloseModal()
      }}
      isDisabled={!avatarUrl}
    >
      {i18n.t('strCommonUI.button.update')}
    </BtnPrimary>
  )

  return (
    <Modal
      closeTimeoutMS={500}
      isOpen={modalOpen}
      className={styles.selectAvatar}
      onRequestClose={() => {
        handleCloseModal()
        onClose()
      }}
      onAfterClose={() => setLoadedImages([])}
      portalClassName={'select-avatar-portal'}
      appElement={document.getElementById(appElement) as HTMLElement}
    >
      <div className={styles.header}>{i18n.t('strSelectAvatar.selectAvatar')}</div>
      <div className={styles.tabs}>
        <Tabs
          className={styles.tabNav}
          onSelect={key => setSelectedTab(key as SelectAvatarTabs)}
          activeKey={selectedTab}
          id='select-avatar-tabs'
        >
          {!uploadDisabled && (
            <Tab
              tabClassName={styles.tab}
              eventKey={SelectAvatarTabs.UPLOAD}
              title={i18n.t('strSelectAvatar.uploadPhoto')}
            />
          )}

          <Tab
            tabClassName={styles.tab}
            eventKey={SelectAvatarTabs.PICK_ITF_AVATAR}
            title={i18n.t('strSelectAvatar.pickItfAvatar')}
          />
        </Tabs>
      </div>
      <div className={styles.content}>
        {selectedTab === SelectAvatarTabs.UPLOAD && (
          <UploadImage
            ref={editorRef}
            loadedImages={loadedImages}
            setLoadedImages={setLoadedImages}
            currentAvatarUrl={currentAvatarUrl}
          />
        )}

        {selectedTab === SelectAvatarTabs.PICK_ITF_AVATAR && (
          <>
            <IcnStyleSwitcher
              onSelect={toggleITFAvatarView}
              heading={`${i18n.t('strSelectAvatar.chooseStyle')}:`}
              selected={ITFAvatarView === people.key ? people : action}
              selections={[action, people]}
            />
            <hr></hr>
            <ITFAvatarSelector setSelectedAvatarUrl={setAvatarUrl} selectedView={ITFAvatarView} />
          </>
        )}
      </div>
      <div className={styles.bottomBtns}>
        <BtnSecondary
          size='small'
          onClick={() => {
            handleCloseModal()
          }}
        >
          {i18n.t('strSelectAvatar.cancel')}
        </BtnSecondary>
        {selectedTab === SelectAvatarTabs.UPLOAD ? (
          <SetUploadedAvatarButton />
        ) : (
          <SetSelectedAvatarButton />
        )}
      </div>
    </Modal>
  )
}

export default SelectItfAvatarModal
